import { createRouter, createWebHistory } from 'vue-router';

// Layouts
import MainLayout from '@/layouts/MainLayout.vue';
import AuthLayout from '@/layouts/AuthLayout.vue';
import PrintLayout from '@/layouts/PrintLayout.vue';
import BlankLayout from '@/layouts/BlankLayout.vue';

// Módulos de rutas
import sales from './sales';
import purchases from './purchases';
import ecommerce from './ecommerce';
import inventory from './inventory';
import funds from './funds';
import config from './config';
import reports from './reports';
import entities from './entities';

// Login estático: es la primera pantalla, conviene que cargue al instante
import Login from '../components/auth/Login.vue';

// Componentes individuales (lazy: se cargan solo al entrar a su ruta)
const ReprintSale = () => import('../components/general/Reprint-sale.vue');
const Downloads = () => import('../components/general/Download.vue');
const Billing = () => import('../components/Billing.vue');
const ReceiptTicket = () => import('../components/general/Receipt-Ticket.vue');
const OrderNoteTicket = () => import('../components/general/Order-notes-Ticket.vue');
const Register = () => import('../components/auth/Register.vue');
const News = () => import('../components/general/Notifications-list.vue');
const Ticket = () => import('../components/general/Ticket.vue');
const Ticketg = () => import('../components/general/TicketGrido.vue');
const Changepassword = () => import('../components/configs/ChangePassword.vue');

import { h } from 'vue';
import ErrorPage from '@/components/ui/ErrorPage.vue';
import { useMeStore } from '@/stores/me';

const FakeError = {
  name: 'FakeError',
  render() {
    return h(ErrorPage, {
      code: 'ERROR 404',
      icon: 'pi pi-compass',
      title: 'No encontramos esta página',
      message: 'Puede que el enlace esté mal escrito o que la página ya no exista.',
    });
  },
};

const BlockError = {
  name: 'BlockError',
  render() {
    return h(ErrorPage, {
      code: 'ERROR 403',
      icon: 'pi pi-lock',
      title: 'No tenés permiso para ver esta sección',
      message: 'Si creés que deberías tener acceso, pedile a un administrador que revise tus permisos.',
    });
  },
};

const routes = [
  // ========== RUTAS CON LAYOUT PRINCIPAL ==========
  {
    path: '/',
    component: MainLayout,
    meta: { requiresAuth: true },
    children: [
      // Dashboard removido - redirect a ventas
      { path: '', redirect: { name: 'Ventas' } },

      // Módulos transaccionales
      ...sales.children.map(route => ({
        ...route,
        path: `ventas/${route.path}`
      })),
      ...purchases.children.map(route => ({
        ...route,
        path: `compras/${route.path}`
      })),
      ...ecommerce.children.map(route => ({
        ...route,
        path: `ecommerce/${route.path}`
      })),
      ...inventory.children.map(route => ({
        ...route,
        path: `inventario/${route.path}`
      })),
      ...funds.children.map(route => ({
        ...route,
        path: `cajas/${route.path}`
      })),
      ...config.children.map(route => ({
        ...route,
        path: `configuracion/${route.path}`
      })),

      // Entidades de primer nivel (clientes, proveedores, etc.)
      ...entities,

      // Reportes
      reports,

      // Páginas individuales
      {
        path: 'facturacion',
        name: 'Billing',
        component: Billing,
      },
      {
        path: 'notificaciones',
        name: 'Notificaciones',
        component: News,
      },
      {
        // Se llamaba 'Mi Perfil', pero la pantalla solo cambia la contraseña:
        // no hay perfil que editar.
        path: 'cambiar-contrasena',
        name: 'Cambiar contraseña',
        component: Changepassword,
        meta: { title: 'Cambiar contraseña' },
      },
      {
        path: 'ventas/reimprimir',
        name: 'ReprintSale',
        component: ReprintSale,
      },
      {
        path: 'descargas',
        name: 'Downloads',
        component: Downloads,
      },

    ],
  },

  // ========== RUTAS CON LAYOUT DE AUTENTICACIÓN ==========
  {
    path: '/auth',
    component: AuthLayout,
    meta: { hideForAuth: true },
    children: [
      {
        path: 'login',
        name: 'Login',
        component: Login,
      },
      {
        path: 'register',
        name: 'Register',
        component: Register,
      },
    ],
  },

  // Redirect para rutas legacy
  {
    path: '/login',
    redirect: '/auth/login',
  },
  {
    path: '/register',
    redirect: '/auth/register',
  },
  {
    path: '/logout',
    name: 'logout',
    beforeEnter: async (to, from, next) => {
      await useMeStore().logout();
      next({ name: 'Login' });
    },
  },

  // ========== RUTAS CON LAYOUT DE IMPRESIÓN ==========
  {
    path: '/ventas/ticket/:sale_id',
    component: PrintLayout,
    meta: { requiresAuth: true },
    children: [
      {
        path: '',
        name: 'Ticket',
        component: Ticket,
        props: true,
      },
    ],
  },
  {
    path: '/ventas/ticket-grido/:sale_id',
    component: PrintLayout,
    meta: { requiresAuth: true },
    children: [
      {
        path: '',
        name: 'Ticketg',
        component: Ticketg,
        props: true,
      },
    ],
  },
  {
    path: '/cobranzas/ticket/:receipt_id',
    component: PrintLayout,
    meta: { requiresAuth: true },
    children: [
      {
        path: '',
        name: 'Receipt-Ticket',
        component: ReceiptTicket,
        props: true,
      },
    ],
  },
  {
    path: '/notas-de-pedido/ticket/:orderNoteId',
    component: PrintLayout,
    meta: { requiresAuth: true },
    children: [
      {
        path: '',
        name: 'Order-notes-Ticket',
        component: OrderNoteTicket,
        props: true,
      },
    ],
  },

  // ========== RUTAS SIN LAYOUT ==========
  {
    path: '/sin-permiso',
    component: BlankLayout,
    children: [
      {
        path: '',
        component: BlockError,
      },
    ],
  },

  // ========== RUTA 404 ==========
  {
    path: '/:notFound(.*)',
    component: BlankLayout,
    children: [
      {
        path: '',
        component: FakeError,
      },
    ],
  },
];

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
});

router.beforeEach((to, from, next) => {
  // Verificar si requiere autenticación
  if (to.matched.some(record => record.meta.requiresAuth)) {
    if (!localStorage.getItem('token')) {
      return next({ name: 'Login' });
    } else {
      if (localStorage.getItem('expired') === 'true' && to.name != 'Billing') {
        return next({ name: 'Billing' });
      }
    }
  }

  // Verificar si debe ocultarse para usuarios autenticados
  if (to.matched.some(record => record.meta.hideForAuth)) {
    if (localStorage.getItem('token')) {
      return next({ name: 'Ventas' });
    }
  }

  // Si ninguna condición especial se cumple, continuar
  next();
});

export default router;
